import { useCreateBelegungen } from '@/domain/dienstplan/features/VisualEditor/hooks/useBelegungen/useCreateBelegungen';
import { Emitter } from '@/domain/dienstplan/features/VisualEditor/utils/emitter/emitter';
import { VisualEditorEvents } from '@/domain/dienstplan/features/VisualEditor/utils/emitter/types';
import { useVisualEditorContext } from '@/domain/dienstplan/features/VisualEditor/VisualEditorContext';
import { StoreContext } from '@/domain/dienstplan/features/VisualEditor/VisualEditorWrapper';

import { useProfile } from '@/hooks';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useStore } from 'zustand';

import { SharedContext } from '@/domain/dienstplan/features/VisualEditor/hooks/useVisualEditor/types';
import { startBelegungLeftResize } from '@/domain/dienstplan/features/VisualEditor/hooks/useVisualEditor/actions/startBelegungLeftResize';
import { startBelegungCreate } from '@/domain/dienstplan/features/VisualEditor/hooks/useVisualEditor/actions/startBelegungCreate';
import { useUpdateBelegung } from '@/domain/dienstplan/features/VisualEditor/hooks/useBelegungen/useUpdateBelegungen';
import { endBelegungCreate } from '@/domain/dienstplan/features/VisualEditor/hooks/useVisualEditor/actions/endBelegungCreate';
import { endBelegungResize } from '@/domain/dienstplan/features/VisualEditor/hooks/useVisualEditor/actions/endBelegungResize';
import { startBelegungRightResize } from '@/domain/dienstplan/features/VisualEditor/hooks/useVisualEditor/actions/startBelegungRightResize';
import { updateBelegung } from '@/domain/dienstplan/features/VisualEditor/hooks/useVisualEditor/actions/updateBelegung';
import { useDeleteBelegung } from '@/domain/dienstplan/features/VisualEditor/hooks/useBelegungen/useDeleteBelegung';
import { updateCursorPosition } from '@/domain/dienstplan/features/VisualEditor/hooks/useVisualEditor/actions/updateCursorPosition';
import { showPopover } from '@/domain/dienstplan/features/VisualEditor/hooks/useVisualEditor/actions/showPopover';
import { DRAG_PADDING } from '@/domain/dienstplan/shared/utils/constants';
import { startBelegungHeightResize } from '@/domain/dienstplan/features/VisualEditor/hooks/useVisualEditor/actions/startBelegungHightResize';

function getEditorElement(): HTMLElement {
  const editor = document.getElementById('visual-editor');
  if (!editor) {
    throw new Error('Element with id "visual-editor" not found');
  }
  return editor;
}

// eslint-disable-next-line max-lines-per-function
export function useVisualEditor() {
  const { manager } = useProfile();
  const { weekday } = useParams();
  const { t } = useTranslation();

  const {
    xScalePoint,
    yScalePoint,
    selectedArbeitsbereich,
    maxMitarbeiterAnzahl,
    yScaleAxis,
    xScaleAxis,
  } = useVisualEditorContext();

  const storeContext = useContext(StoreContext);
  if (!storeContext) {
    throw new Error(
      'useVisualEditor must be used within a VisualEditorWrapper',
    );
  }
  const store = useStore(storeContext, (s) => s);
  const {
    data: { belegungen },
    ui,
  } = store;

  const [createBelegung] = useCreateBelegungen();
  const [updateBelegungMutation] = useUpdateBelegung();
  const [deleteBelegung] = useDeleteBelegung();

  const sharedContext: SharedContext = {
    belegungen,
    selectedArbeitsbereich: selectedArbeitsbereich!,
    xScalePoint,
    yScalePoint,
    yScaleAxis,
    xScaleAxis,
    weekday: weekday!,
    manager: manager!,
    t,
    store,
    createBelegung,
    updateBelegung: updateBelegungMutation,
    deleteBelegung,
    maxMitarbeiterAnzahl: maxMitarbeiterAnzahl.yAxisValue,
  };

  const emitter = new Emitter<VisualEditorEvents>();
  emitter.on('belegungMouseMove', ({ belegung, point, event }) => {
    event.preventDefault();
    if (ui.isCreatingNewBelegung) {
      return;
    }

    showPopover({ ctx: sharedContext, point, event });

    const editor = getEditorElement();

    if (point.x <= Math.round(xScalePoint(belegung.startX)) + DRAG_PADDING) {
      editor.style.cursor = 'ew-resize';
    } else if (
      point.x >=
      Math.round(xScalePoint(belegung.endeX)) - DRAG_PADDING
    ) {
      editor.style.cursor = 'ew-resize';
    } else if (
      point.y >=
        Math.round(yScalePoint(belegung.belegung.personenanzahlMax)) -
          DRAG_PADDING &&
      point.y <=
        Math.round(yScalePoint(belegung.belegung.personenanzahlMax)) +
          DRAG_PADDING
    ) {
      editor.style.cursor = 'ns-resize';
    } else {
      editor.style.cursor = 'default';
    }
  });

  emitter.on('belegungMouseDown', ({ belegung, point }) => {
    if (ui.isCreatingNewBelegung) {
      return;
    }

    if (
      point.y >=
        Math.round(yScalePoint(belegung.belegung.personenanzahlMax)) -
          DRAG_PADDING &&
      point.y <=
        Math.round(yScalePoint(belegung.belegung.personenanzahlMax)) +
          DRAG_PADDING
    ) {
      startBelegungHeightResize({
        currentPoint: point,
        currentBelegung: belegung,
        ctx: sharedContext,
      });
      return;
    }

    if (point.x <= Math.round(xScalePoint(belegung.startX)) + DRAG_PADDING) {
      startBelegungLeftResize({
        currentPoint: point,
        currentBelegung: belegung,
        ctx: sharedContext,
      });
    } else if (
      point.x >=
      Math.round(xScalePoint(belegung.endeX)) - DRAG_PADDING
    ) {
      startBelegungRightResize({
        currentPoint: point,
        currentBelegung: belegung,
        ctx: sharedContext,
      });
    }
  });

  // oxlint-disable-next-line complexity
  emitter.on('belegungMouseLeave', ({ event }) => {
    const editor = getEditorElement();
    editor.style.cursor = 'default';

    const related = event.relatedTarget as HTMLElement | null;
    if (
      related?.closest('.react-popover-trigger') ||
      related?.closest('tspan') ||
      related?.closest('.belegung-popover-content')
    ) {
      return;
    }
    sharedContext.store.setHoveredBelegung(undefined);
    sharedContext.store.setIsPopoverOpen(false);
    sharedContext.store.setPopoverPosition(undefined);
  });

  emitter.on('belegungMouseUp', () => {
    if (ui.isCreatingNewBelegung) {
      return;
    }

    const editor = getEditorElement();
    editor.style.cursor = 'default';
    if (ui.isResizingStart) {
      sharedContext.store.setIsResizingStart(false);
    } else if (ui.isResizingEnde) {
      sharedContext.store.setIsResizingEnde(false);
    }
  });

  emitter.on('visualEditorMouseDown', ({ point }) => {
    startBelegungCreate({ ctx: sharedContext, currentPoint: point });
  });

  emitter.on('visualEditorMouseMove', ({ point }) => {
    updateCursorPosition({ ctx: sharedContext, currentPoint: point });
    updateBelegung({ ctx: sharedContext, currentPoint: point });
  });

  emitter.on('visualEditorMouseUp', () => {
    if (ui.isResizingStart || ui.isResizingEnde) {
      endBelegungResize({ ctx: sharedContext });
    } else {
      endBelegungCreate({ ctx: sharedContext });
    }
  });

  emitter.on('visualEditorMouseLeave', () => {
    if (ui.isResizingStart || ui.isResizingEnde) {
      endBelegungResize({ ctx: sharedContext });
    } else {
      endBelegungCreate({ ctx: sharedContext });
    }
    sharedContext.store.setIsCreatingNewBelegung(false);
    sharedContext.store.setIsResizingEnde(false);
    sharedContext.store.setIsResizingStart(false);
    sharedContext.store.setStartBelegungPosition(undefined);
  });

  return { emitter, belegungen, store };
}
